import { useEffect, useState } from "react";
import { useRef } from "react";
import { useClickOutsideBlock } from "../../helpers/useClickOutside";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { enumPermissions } from "../../consts/management/users/users";
import { useDispatch, useSelector } from "react-redux";
import { getLocationsOfReports, ReportsLoadingKeys, updateLocationsOfReports } from "../../slices/reportsSlice";
import { LoaderSpinner } from "../LoaderSpinner";
import { OverlayGrayBackground } from "../OverlayGrayBackground";

const accessCategories = Object.entries(enumPermissions).map(([key, label]) => ({ key, label }));

export const ReportsBulkAccessModal = ({ 
  isOpen, 
  reportIds, 
  cancelClick,
  marginFromSidebar
}) => {
  const dispatch = useDispatch();
  const modalRef = useRef(null);
  useClickOutsideBlock(modalRef, cancelClick);

  const { locationsOfReports, loadingKeys, loading } = useSelector((state) => state.reports);
  const [accessDraft, setAccessDraft] = useState({});
  const checkboxRefs = useRef({});

  useEffect(() => {
    if (!isOpen || !Array.isArray(reportIds) || reportIds.length === 0) {
      setAccessDraft({});
      return;
    }
    dispatch(getLocationsOfReports({}));
  }, [isOpen, reportIds, dispatch]);

  useEffect(() => {
    if (!isOpen || !reportIds?.length) return;
    const nextDraft = {};
    const selectedGroups = reportIds.map((id) => {
      const row = (locationsOfReports || []).find((item) => item?.report_name === id);
      return new Set(String(row?.tab_name || "").split(",").map((s) => s.trim()).filter(Boolean));
    });
    accessCategories.forEach(({ key }) => {
      const count = selectedGroups.filter((groups) => groups.has(key)).length;
      nextDraft[key] = count === selectedGroups.length ? "add" : count === 0 ? "remove" : "leave";
    });
    setAccessDraft(nextDraft);
  }, [isOpen, locationsOfReports, reportIds]);

  useEffect(() => {
    accessCategories.forEach(({ key }) => {
      const checkbox = checkboxRefs.current[key];
      if (checkbox) checkbox.indeterminate = accessDraft[key] === "leave";
    });
  }, [accessDraft]);

  const handleSave = async () => {
    if (!reportIds?.length) return;
    const locations = reportIds.map((report_name) => {
      const row = (locationsOfReports || []).find((item) => item?.report_name === report_name);
      const groups = new Set(String(row?.tab_name || "").split(",").map((s) => s.trim()).filter(Boolean));
      accessCategories.forEach(({ key }) => {
        if (accessDraft[key] === "add") groups.add(key);
        if (accessDraft[key] === "remove") groups.delete(key);
      });
      return {
        report_name,
        tab_name: groups.size > 0 ? Array.from(groups).join(",") : null,
      };
    });
    await dispatch(updateLocationsOfReports({ locations })).unwrap();
    await dispatch(getLocationsOfReports({}));
    cancelClick?.();
  };

  const updateLocationsIsLoading = () =>
    loading && loadingKeys.includes(ReportsLoadingKeys.updateLocationsData);
  const actionLabels = {
    add: "Add to all",
    remove: "Remove from all",
    leave: "Leave as-is",
  };
  const selectedLabels = accessCategories
    .filter(({ key }) => accessDraft[key])
    .map(({ key, label }) => `${label}: ${actionLabels[accessDraft[key]]}`);

  if (!isOpen || !reportIds?.length) return null;

  return (
    <div
      aria-hidden="true"
      className="fixed inset-0 z-40 flex items-center justify-center overflow-y-auto overflow-x-hidden p-4"
    >
      <div className="fixed inset-0 bg-gray-700/40" />
      <div
        ref={modalRef}
        style={{
          '--content-width': marginFromSidebar ? `calc(1380px - ${marginFromSidebar}px)` : "1380px",
          'marginLeft': marginFromSidebar ? `${marginFromSidebar}px` : "0",
        }}
        className={`relative z-50 w-full max-w-lg rounded-lg border border-gray-200 bg-white shadow-xl w-full max-w-[min(96vw,var(--content-width))] max-h-[90vh] overflow-y-auto`}
      >
        {updateLocationsIsLoading() && <OverlayGrayBackground />}
        {updateLocationsIsLoading() && (
          <div className="absolute top-1/2 left-1/2 z-50 -translate-x-1/2 -translate-y-1/2">
            <LoaderSpinner colorSpinner="black" height="120" width="120" border="8" />
          </div>
        )}
        <div className="flex items-start justify-between gap-4 border-b border-gray-200 px-5 py-4">
          <div className="space-y-1 pr-6">
            <h2 className="text-lg font-semibold text-gray-900">Bulk edit visibility</h2>
            <p className="text-sm text-gray-500">
              Apply access groups to {reportIds.length} selected reports. This replaces the current visibility for those reports.
            </p>
          </div>
          <button type="button" onClick={cancelClick} className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100">
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
        <div className="space-y-5 px-5 py-5">
          <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-900">
            {selectedLabels.length > 0 ? selectedLabels.join(" · ") : "No access groups selected"}
          </div>
          <div className="space-y-1 rounded-md border border-gray-200 bg-white p-2">
            {accessCategories.map(({ key, label }) => {
              const action = accessDraft[key] || "leave";
              return (
                <label key={key} className={`flex items-center gap-3 rounded px-3 py-2 text-sm transition-colors ${action === "add" ? "bg-gray-100" : "hover:bg-gray-50"}`}>
                  <input
                    ref={(node) => {
                      checkboxRefs.current[key] = node;
                    }}
                    type="checkbox"
                    checked={action === "add"}
                    onChange={() => setAccessDraft((current) => ({
                      ...current,
                      [key]: current[key] === "add"
                        ? "remove"
                        : current[key] === "remove"
                          ? "leave"
                          : "add",
                    }))}
                    className="h-4 w-4 rounded border-gray-300 accent-gray-900"
                  />
                  <span className="flex-1 font-medium text-gray-900">{label}</span>
                  <span className="text-xs text-gray-500">{actionLabels[action]}</span>
                </label>
              );
            })}
          </div>
          <div className="flex items-center justify-end gap-2">
            <button type="button" onClick={cancelClick} className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50">Cancel</button>
            <button type="button" onClick={handleSave} disabled={updateLocationsIsLoading()} className="px-4 py-2 bg-gray-900 text-white rounded-md text-sm hover:bg-gray-800 disabled:opacity-50">Apply to selected</button>
          </div>
        </div>
      </div>
    </div>
  );
};
